import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Stock's admin API (CONTEXT.md): the locations stock lies at, the stock per variant per location,
 * and the movements that change it. A stock level is never set directly: every change is a
 * movement, with its kind, the change, a reason and the employee. Errors come back as
 * `{ _tag, message }` with an HTTP status; the tags are named on each procedure. An invalid body or
 * query answers 400.
 */

// ---------------------------------------------------------------------------------------------
// Locations

const LocationName = z.string().trim().min(1).max(100);

/**
 * Available (`CONTEXT.md`): the stock on hand over every location minus the reservations that still
 * hold stock, never below 0. The API counts what holds (`apps/api/src/lib/availability.ts`); the
 * admin's help shows it in its sandbox with the same rule.
 */
export const availableOf = (onHand: number, reserved: number): number => Math.max(0, onHand - reserved);

/** A live location. */
export const Location = z.object({
	id: z.string(),
	name: z.string(),
	/** Where stock goes when nobody names a location. Exactly one location is the default. */
	isDefault: z.boolean(),
	/** The stock at this location, summed over every variant. Only a location at 0 can be removed. */
	quantity: z.number().int(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type Location = z.infer<typeof Location>;

export const LocationBody = z.object({ name: LocationName });
export type LocationBody = z.infer<typeof LocationBody>;

const ByLocation = z.object({ locationId: z.string() });

// ---------------------------------------------------------------------------------------------
// Stock per variant

/** `receipt`, `sale`, `correction`, `transfer_to_fulfilment`, `return`. */
export const StockMovementKind = z.enum([
	"receipt",
	"sale",
	"correction",
	"transfer_to_fulfilment",
	"return",
]);
export type StockMovementKind = z.infer<typeof StockMovementKind>;

/** The stock of one variant at one live location. */
export const StockLevel = z.object({
	locationId: z.string(),
	locationName: z.string(),
	isDefault: z.boolean(),
	quantity: z.number().int(),
});
export type StockLevel = z.infer<typeof StockLevel>;

/** A variant (not deleted) with its stock at every live location, the default first. */
export const VariantStock = z.object({
	variantId: z.string(),
	productId: z.string(),
	productName: z.string(),
	/** The value names in option order (`XXL / Blauw`); the product's name without options. */
	variantName: z.string(),
	sku: z.string(),
	ean: z.string().nullable(),
	active: z.boolean(),
	/** "Bijna op" from 1 up to this many pieces (the product's `lowStockThreshold`); 0 never warns. */
	lowStockThreshold: z.number().int(),
	/**
	 * A variant of a `composed` bundle: it has no stock of its own (its availability follows from
	 * the components), so no movements are booked on it (`ComposedBundle`).
	 */
	composedBundle: z.boolean(),
	/**
	 * Its product is archived (spec, A2): read-only for staff until it is restored, so no movements
	 * are booked on it (`ProductArchived`); what orders give back still comes in.
	 */
	archived: z.boolean(),
	levels: z.array(StockLevel),
	/** The levels summed. */
	quantity: z.number().int(),
	/** What can still be sold: `quantity` minus the reservations that still hold stock, never below 0. */
	available: z.number().int(),
});
export type VariantStock = z.infer<typeof VariantStock>;

/**
 * `low`: 1 up to the product's `lowStockThreshold` pieces; `out`: none. Summed over every live
 * location.
 */
export const StockFilter = z.enum(["low", "out"]);
export type StockFilter = z.infer<typeof StockFilter>;

/**
 * The stock overview and the quick correction's search, as the admin keeps it in its URL. Without
 * `search`, every variant.
 */
export const StockListQuery = z.object({
	/**
	 * Part of the product's name, the variant's name, its SKU or its EAN, any case. A variant whose
	 * SKU or EAN is exactly this comes first, so a scanned barcode lands on top.
	 */
	search: z.string().trim().max(200).optional(),
	/** Only variants low on stock, or out of it. */
	stock: StockFilter.optional(),
	/** Only active variants of active products: what the shop sells (the start page's work queue). */
	activeOnly: z.enum(["yes"]).optional(),
	/** 1-based. */
	page: z.coerce.number().int().min(1).default(1),
	pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type StockListQuery = z.infer<typeof StockListQuery>;

/** One page of variants with their stock, by product name and variant name. Deleted variants never appear. */
export const VariantStockPage = z.object({
	items: z.array(VariantStock),
	total: z.number().int(),
	page: z.number().int(),
	pageSize: z.number().int(),
});
export type VariantStockPage = z.infer<typeof VariantStockPage>;

/** One logged change, newest first in a history. */
export const StockMovement = z.object({
	id: z.string(),
	variantId: z.string(),
	locationId: z.string(),
	locationName: z.string(),
	kind: StockMovementKind,
	/** The signed change: positive adds stock, negative takes it off. */
	quantity: z.number().int(),
	/** The level at this location right after this movement. */
	quantityAfter: z.number().int(),
	reason: z.string().nullable(),
	/** The employee who booked it; `null` for a movement the system booked (a sale, a return). */
	employeeId: z.string().nullable(),
	/** That employee's name (also after they were deactivated); `null` with `employeeId`. */
	employeeName: z.string().nullable(),
	createdAt: z.iso.datetime(),
});
export type StockMovement = z.infer<typeof StockMovement>;

const Reason = z.string().trim().min(1).max(500);
const Count = z.number().int().min(1).max(1_000_000);

/**
 * A movement the admin books. `locationId` left out means the default location.
 *
 * - `receipt`: `quantity` arrived (a positive count), optional reason.
 * - `correction`: `delta` is the signed change (`-2` after two broke), with a required reason. A
 *   correction never takes a level below 0.
 * - `transfer_to_fulfilment`: `quantity` went to the fulfilment partner (a positive count). It
 *   lowers our own stock, never below 0.
 *
 * Sales and returns are booked by checkout and refunds, not here.
 */
export const StockMovementBody = z.discriminatedUnion("kind", [
	z.object({
		kind: z.literal("receipt"),
		locationId: z.string().min(1).optional(),
		quantity: Count,
		reason: Reason.optional(),
	}),
	z.object({
		kind: z.literal("transfer_to_fulfilment"),
		locationId: z.string().min(1).optional(),
		quantity: Count,
		reason: Reason.optional(),
	}),
	z.object({
		kind: z.literal("correction"),
		locationId: z.string().min(1).optional(),
		delta: z
			.number()
			.int()
			.min(-1_000_000)
			.max(1_000_000)
			.refine((delta) => delta !== 0, "A correction changes the stock"),
		reason: Reason,
	}),
]);
export type StockMovementBody = z.infer<typeof StockMovementBody>;

/** What booking a movement answers: the movement, and the variant's stock after it. */
export const StockMovementResult = z.object({ movement: StockMovement, stock: VariantStock });
export type StockMovementResult = z.infer<typeof StockMovementResult>;

const ByVariant = z.object({ variantId: z.string() });

/** A variant's movement history, as the admin keeps it in its URL. */
export const StockMovementQuery = z.object({
	/** Only movements of this kind. */
	kind: StockMovementKind.optional(),
	/** 1-based. */
	page: z.coerce.number().int().min(1).default(1),
	pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type StockMovementQuery = z.infer<typeof StockMovementQuery>;

/** One page of a variant's movements, newest first, with the number that match. */
export const StockMovementPage = z.object({
	items: z.array(StockMovement),
	total: z.number().int(),
	page: z.number().int(),
	pageSize: z.number().int(),
});
export type StockMovementPage = z.infer<typeof StockMovementPage>;

export const StockContract = {
	/** Every live location: the default first, then by name. */
	listLocations: oc.route({ method: "GET", path: "/admin/locations" }).output(z.array(Location)),
	/** `LocationNameTaken` (409). */
	createLocation: oc
		.route({ method: "POST", path: "/admin/locations", successStatus: 201 })
		.input(LocationBody)
		.output(Location),
	/** `LocationNotFound` (404), `LocationNameTaken` (409). */
	renameLocation: oc
		.route({ method: "PATCH", path: "/admin/locations/{locationId}" })
		.input(LocationBody.extend(ByLocation.shape))
		.output(Location),
	/**
	 * Removes a location that holds no stock. `LocationNotFound` (404), `DefaultLocation` (409: the
	 * default stays), `LocationHasStock` (409).
	 */
	removeLocation: oc
		.route({ method: "DELETE", path: "/admin/locations/{locationId}", successStatus: 204 })
		.input(ByLocation),
	/**
	 * Variants with their stock, found by name, SKU or EAN: the stock screen and the quick correction
	 * on a phone (which then books a `correction` with `recordMovement`).
	 */
	list: oc.route({ method: "GET", path: "/admin/stock" }).input(StockListQuery).output(VariantStockPage),
	/** `VariantNotFound` (404, also for a deleted variant). */
	getVariant: oc
		.route({ method: "GET", path: "/admin/stock/variants/{variantId}" })
		.input(ByVariant)
		.output(VariantStock),
	/**
	 * Books a movement: the level and its movement are written in one atomic batch.
	 * `VariantNotFound` (404), `LocationNotFound` (404), `InsufficientStock` (409: it would take the
	 * level below 0), `ComposedBundle` (409: a composed bundle's variant has no stock of its own;
	 * book on its components), `ProductArchived` (409: restore the product first).
	 */
	recordMovement: oc
		.route({ method: "POST", path: "/admin/stock/variants/{variantId}/movements", successStatus: 201 })
		.input(z.intersection(StockMovementBody, ByVariant))
		.output(StockMovementResult),
	/** The variant's movements, newest first, at every location. `VariantNotFound` (404). */
	listMovements: oc
		.route({ method: "GET", path: "/admin/stock/variants/{variantId}/movements" })
		.input(StockMovementQuery.extend(ByVariant.shape))
		.output(StockMovementPage),
};
